import * as React from 'react';
import { useQueryClient } from 'react-query';

import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  useGetOrganizationInvitationsQuery,
  useGetOrganizationMembersQuery,
  useGetOrganizationQuery,
  useListOrganizationsQuery,
} from 'src/ui/graph/types';

export enum OrganizationRole {
  OWNER = 'OWNER',
  ADMIN = 'ADMIN',
  MEMBER = 'MEMBER',
}

export type OrganizationMember = {
  firstName: string;
  lastName: string;
  email: string;
  role: OrganizationRole;
};

export type OrganizationInvitation = {
  id: string;
  email: string;
  role: OrganizationRole;
  created: Date;
  expired: boolean;
};

export const useListOrganizations = () =>
  useListOrganizationsQuery(graphQLClient);

export const useGetOrganization = (org: string | undefined) =>
  useGetOrganizationQuery(
    graphQLClient,
    { org_domain: org },
    { enabled: Boolean(org) }
  );

export const useGetOrganizationMembers = (org: string | undefined) =>
  useGetOrganizationMembersQuery(
    graphQLClient,
    { org_domain: org },
    { enabled: Boolean(org) }
  );

export const useGetOrganizationInvitations = (
  org: string | undefined,
  enabled?: boolean
) =>
  useGetOrganizationInvitationsQuery(
    graphQLClient,
    { org_domain: org },
    { enabled: Boolean(org && enabled) }
  );

export const useOrganization = (org: string | undefined) => {
  const isAdmin = useIsOrganizationAdmin(org);
  const {
    data: organizationData,
    isLoading: isLoadingOrganization,
  } = useGetOrganization(org);
  const {
    data: memberData,
    isLoading: isLoadingMembers,
  } = useGetOrganizationMembers(org);
  const {
    data: invitationData,
    isLoading: isLoadingInvitations,
  } = useGetOrganizationInvitations(org, isAdmin);

  const organization = organizationData?.getOrganization;

  const membersByEmail = React.useMemo(
    () =>
      (memberData?.getOrganizationMembers || []).reduce<
        Record<string, OrganizationMember>
      >((acc, member) => {
        if (member && member.user.email) {
          acc[member.user.email] = {
            firstName: member.user.first_name || '',
            lastName: member.user.last_name || '',
            email: member.user.email,
            role: member.role as OrganizationRole,
          };
        }
        return acc;
      }, {}),
    [memberData]
  );

  const invitationsByEmail = React.useMemo(
    () =>
      (invitationData?.getOrganizationInvitations || []).reduce<
        Record<string, OrganizationInvitation>
      >((acc, invitation) => {
        if (
          invitation &&
          invitation.email &&
          !membersByEmail?.[invitation.email]
        ) {
          acc[invitation.email] = {
            id: invitation.id,
            email: invitation.email,
            role: invitation.organization_role as OrganizationRole,
            created: new Date(invitation.created),
            expired: Boolean(invitation.expired),
          };
        }
        return acc;
      }, {}),
    [invitationData, membersByEmail]
  );

  return {
    invitationsByEmail,
    isLoadingInvitations,
    isLoadingMembers,
    isLoadingOrganization,
    membersByEmail,
    organization,
  };
};

export const useOrganizationRole = (org: string | undefined) => {
  const { data: organizationsData } = useListOrganizations();
  const organization = organizationsData?.listOrganizations?.find(
    (organization) => organization?.domain === org
  );
  return organization?.role;
};

export const useIsOrganizationAdmin = (org: string | undefined) => {
  const role = useOrganizationRole(org);
  return role === OrganizationRole.ADMIN || role === OrganizationRole.OWNER;
};

export const useIsOrganizationOwner = (org: string | undefined) => {
  const role = useOrganizationRole(org);
  return role === OrganizationRole.OWNER;
};

export const useInvalidateOrganization = (org: string) => {
  const queryClient = useQueryClient();

  return React.useCallback(() => {
    queryClient.invalidateQueries(useListOrganizationsQuery.getKey());
    queryClient.invalidateQueries(
      useGetOrganizationQuery.getKey({ org_domain: org })
    );
  }, [queryClient, org]);
};

export const useInvalidateOrganizationMembers = (org: string) => {
  const queryClient = useQueryClient();

  return React.useCallback(() => {
    queryClient.invalidateQueries(
      useGetOrganizationMembersQuery.getKey({ org_domain: org })
    );
    queryClient.invalidateQueries(
      useGetOrganizationInvitationsQuery.getKey({ org_domain: org })
    );
  }, [queryClient, org]);
};
